(() => {
  "use strict";

  const CM_DRAFTS = {
    API_BASE: (window.CM_SVG_API_BASE || "https://cm-editor-server.onrender.com").replace(/\/+$/, ""),
    AUTOSAVE_MS: 30000, // backup save every 30 seconds while dirty
    CHANGE_SAVE_DELAY_MS: 12000,
    HASH_POLL_MS: 4000,
    MANUAL_BUTTON_ID: "pamela-save-design-btn",
    TEMPLATE_BUTTON_ID: "pamela-save-template-btn",
    EMAIL_ON_CREATE: false,
    LINK_EXPIRES_DAYS: 7,
    DELETE_AFTER_DAYS: 30,
    EXPIRED_MESSAGE:
      "This design link has expired. Designs are available for 7 days from the last save. Contact us and we'll be happy to resend access if the design is still available.",
    DELETED_MESSAGE:
      "This design is no longer available. Please contact us and we'll be happy to help you start a new design."
  };

  let __cmDraftBooted = false;
  let __cmDraftDirty = false;
  let __cmLastHash = "";
  let __cmAutosaveTimer = null;
  let __cmHashPollTimer = null;
  let __cmDebouncedSaveTimer = null;
  let __cmBootPromise = null;
  let __cmEnsureIdentityPromise = null;
  let __cmReadOnly = false;
  let __cmResumeOverlayEl = null;

  function setResumeStartGuidanceSuppressed(suppressed) {
    const styleId = "cm-resume-guidance-suppressed-styles";
    let style = document.getElementById(styleId);

    if (suppressed) {
      if (!style) {
        style = document.createElement("style");
        style.id = styleId;
        style.textContent =
          "#pamela-start-guidance-tip{display:none !important;}" +
          "#pamela-editor-toolbar .pamela-start-guidance-target{" +
          "animation:none !important;box-shadow:none !important;background:inherit !important;" +
          "}";
        document.head.appendChild(style);
      }

      document
        .querySelectorAll("#pamela-editor-toolbar .pamela-start-guidance-target")
        .forEach((el) => el.classList.remove("pamela-start-guidance-target"));

      const tip = document.getElementById("pamela-start-guidance-tip");
      if (tip) tip.remove();

      window.__pamelaStartGuidanceShown = true;
      return;
    }

    if (style) style.remove();
  }

  function setResumeRestoreOverlayVisible(visible) {
    const nextVisible = !!visible;
    const stageWrap = document.getElementById("pamela-stageWrap");
    if (!stageWrap || !stageWrap.parentNode) return;

    if (nextVisible) {
      if (__cmResumeOverlayEl && __cmResumeOverlayEl.isConnected) return;

      const host = stageWrap.parentNode;
      if (host && host.style && !host.style.position) {
        host.style.position = "relative";
      }

      const overlay = document.createElement("div");
      overlay.id = "cm-resume-restore-overlay";
      overlay.setAttribute("aria-live", "polite");
      overlay.style.cssText =
        "position:absolute;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;" +
        "background:rgba(255,255,255,0.82);backdrop-filter:blur(2px);";

      const card = document.createElement("div");
      card.style.cssText =
        "padding:14px 18px;border:1px solid #ddd;border-radius:12px;background:#fff;color:#111;" +
        "font:800 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;box-shadow:0 10px 30px rgba(0,0,0,0.08);";
      card.textContent = "Loading your saved design...";

      overlay.appendChild(card);
      host.appendChild(overlay);
      __cmResumeOverlayEl = overlay;
      return;
    }

    if (__cmResumeOverlayEl && __cmResumeOverlayEl.parentNode) {
      __cmResumeOverlayEl.parentNode.removeChild(__cmResumeOverlayEl);
    }
    __cmResumeOverlayEl = null;
  }

  function getEditor() {
    return (
      window.pamelaMonumentEditor ||
      window.pamelaEditor ||
      window.editor ||
      window.PAMELA_EDITOR ||
      window.__PAMELA_EDITOR__ ||
      null
    );
  }

  function waitForEditor(timeoutMs = 20000) {
    return new Promise((resolve, reject) => {
      const started = Date.now();

      (function tick() {
        const ed = getEditor();
        const bootDone = window.__PAMELA_EDITOR_BOOT_DONE__ === true;

        if (ed && ed.stage && ed.layers && bootDone) {
          resolve(ed);
          return;
        }

        if (Date.now() - started > timeoutMs) {
          reject(new Error("Editor instance not ready."));
          return;
        }

        setTimeout(tick, 150);
      })();
    });
  }

  function getLeadId() {
    return String(window.cmLeadId || localStorage.getItem("cmLeadId") || "").trim();
  }

  function getTokenFromUrl() {
    try {
      const url = new URL(window.location.href);
      return String(url.searchParams.get("token") || "").trim();
    } catch (_) {
      return "";
    }
  }

  function getDraftToken() {
    return String(window.cmDraftToken || "").trim();
  }

  function getDesignId() {
    return String(window.cmDesignId || "").trim();
  }

  function getReadOnlyFromUrl() {
    try {
      const url = new URL(window.location.href);
      const direct = String(
        url.searchParams.get("readonly") ||
        url.searchParams.get("read_only") ||
        url.searchParams.get("view_only") ||
        ""
      ).trim().toLowerCase();

      if (["1", "true", "yes", "readonly", "read-only"].includes(direct)) {
        return true;
      }

      const mode = String(url.searchParams.get("mode") || "").trim().toLowerCase();
      return ["readonly", "read-only", "view", "view-only"].includes(mode);
    } catch (_) {
      return false;
    }
  }

  function getReferrerUrl() {
    try {
      const raw = String(document.referrer || "").trim();
      if (!raw) return null;
      return new URL(raw, window.location.origin);
    } catch (_) {
      return null;
    }
  }

  function shouldIgnoreResumeTokenForFreshStart() {
    const referrer = getReferrerUrl();
    if (!referrer) return false;

    const path = String(referrer.pathname || "").trim().toLowerCase();
    return path.includes("/products/");
  }

  function stripDraftParamsFromUrl() {
    try {
      const url = new URL(window.location.href);
      let changed = false;

      [
        "token",
        "readonly",
        "read_only",
        "view_only"
      ].forEach((key) => {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });

      const mode = String(url.searchParams.get("mode") || "").trim().toLowerCase();
      if (mode === "readonly" || mode === "read-only" || mode === "view" || mode === "view-only") {
        url.searchParams.delete("mode");
        changed = true;
      }

      if (changed && window.history && typeof window.history.replaceState === "function") {
        window.history.replaceState({}, "", url.toString());
      }
    } catch (_) {}
  }

  function isReadOnlyDraft(draft) {
    if (!draft || typeof draft !== "object") return false;

    const truthyKeys = [
      "read_only",
      "readonly",
      "view_only",
      "locked",
      "is_locked",
      "order_locked",
      "checkout_completed",
      "checkout_complete",
      "purchased",
      "ordered"
    ];

    for (const key of truthyKeys) {
      if (draft[key] === true) return true;
    }

    const valuedKeys = [
      "order_id",
      "order_number",
      "ordered_at",
      "purchased_at",
      "checkout_completed_at",
      "locked_at",
      "read_only_at"
    ];

    for (const key of valuedKeys) {
      if (String(draft[key] || "").trim()) return true;
    }

    const status = String(draft.status || draft.state || draft.draft_status || "").trim().toLowerCase();
    return ["ordered", "completed", "complete", "locked", "read-only", "readonly", "view-only"].includes(status);
  }

  function parseDraftDate(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;

    const time = Date.parse(raw);
    if (!Number.isFinite(time)) return null;

    const date = new Date(time);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function getDraftCreatedAt(draft) {
    if (!draft || typeof draft !== "object") return null;

    return (
      parseDraftDate(draft.created_at) ||
      parseDraftDate(draft.createdAt) ||
      parseDraftDate(draft.created) ||
      parseDraftDate(draft.sent_at) ||
      parseDraftDate(draft.sentAt) ||
      parseDraftDate(draft.email_sent_at) ||
      parseDraftDate(draft.emailSentAt) ||
      parseDraftDate(draft.editor_state_json?.created_at) ||
      parseDraftDate(draft.editor_state_json?.saved_at)
    );
  }

  function getDraftAgeDays(draft) {
    const createdAt = getDraftCreatedAt(draft);
    if (!createdAt) return 0;
    return Math.floor((Date.now() - createdAt.getTime()) / 86400000);
  }

  function getDraftLifecycleStatus(draft) {
    const status = String(draft?.status || draft?.state || draft?.draft_status || "").trim().toLowerCase();
    if (["deleted", "removed", "purged", "unavailable"].includes(status)) return "deleted";
    if (["archived", "expired"].includes(status)) return "expired";

    const expiresAt =
      parseDraftDate(draft?.expires_at) ||
      parseDraftDate(draft?.expiresAt) ||
      parseDraftDate(draft?.link_expires_at) ||
      parseDraftDate(draft?.linkExpiresAt);
    const deleteAt =
      parseDraftDate(draft?.delete_after_at) ||
      parseDraftDate(draft?.deleteAfterAt) ||
      parseDraftDate(draft?.delete_at) ||
      parseDraftDate(draft?.deleteAt) ||
      parseDraftDate(draft?.purge_at) ||
      parseDraftDate(draft?.purgeAt);

    if (deleteAt && Date.now() >= deleteAt.getTime()) return "deleted";
    if (expiresAt && Date.now() >= expiresAt.getTime()) return "expired";

    const ageDays = getDraftAgeDays(draft);
    if (ageDays >= CM_DRAFTS.DELETE_AFTER_DAYS) return "deleted";
    if (ageDays >= CM_DRAFTS.LINK_EXPIRES_DAYS) return "expired";

    return "active";
  }

  function addDays(date, days) {
    const base = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
    return new Date(base.getTime() + (Number(days) || 0) * 86400000);
  }

  async function markDraftLifecycle(token, draft, lifecycleStatus) {
    const t = String(token || "").trim();
    if (!t || !draft || typeof draft !== "object") return;

    const designId = String(draft.design_id || draft.designId || "").trim();
    if (!designId) return;

    const createdAt = getDraftCreatedAt(draft) || new Date();
    const nowIso = new Date().toISOString();
    const deleteAfterIso = addDays(createdAt, CM_DRAFTS.DELETE_AFTER_DAYS).toISOString();

    try {
      await fetch(`${CM_DRAFTS.API_BASE}/api/save-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: t,
          design_id: designId,
          status: lifecycleStatus === "deleted" ? "deleted" : "archived",
          draft_status: lifecycleStatus === "deleted" ? "deleted" : "archived",
          archived_at: lifecycleStatus === "expired" ? nowIso : (draft.archived_at || draft.archivedAt || nowIso),
          deleted_at: lifecycleStatus === "deleted" ? nowIso : undefined,
          link_expires_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
          archive_after_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
          delete_after_days: CM_DRAFTS.DELETE_AFTER_DAYS,
          delete_after_at: deleteAfterIso,
          editor_state: draft.editor_state_json || draft.editor_state || {},
          thumbnail_data: draft.thumbnail_data || draft.thumbnailData || null
        })
      });
    } catch (err) {
      console.warn("Draft lifecycle update failed:", err);
    }
  }

  function applyExpiredDesignMode(message) {
    applyReadOnlyMode(message);

    try {
      const overlayId = "cm-expired-design-overlay";
      let overlay = document.getElementById(overlayId);
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.id = overlayId;
        overlay.setAttribute("role", "alert");
        overlay.style.cssText =
          "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;" +
          "padding:24px;background:rgba(255,255,255,0.92);backdrop-filter:blur(2px);";

        const card = document.createElement("div");
        card.style.cssText =
          "max-width:560px;width:100%;padding:24px;border:1px solid #ddd;border-radius:10px;background:#fff;color:#111;" +
          "box-shadow:0 18px 50px rgba(0,0,0,0.14);font:500 16px/1.5 system-ui,-apple-system,Segoe UI,Roboto,Arial;text-align:center;";

        const title = document.createElement("h1");
        title.style.cssText = "margin:0 0 10px;font:900 24px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;";
        title.textContent = "Design Link Expired";

        const body = document.createElement("p");
        body.style.cssText = "margin:0;color:#333;";
        body.textContent = message || CM_DRAFTS.EXPIRED_MESSAGE;

        card.appendChild(title);
        card.appendChild(body);
        overlay.appendChild(card);
        document.body.appendChild(overlay);
      }
    } catch (_) {}
  }

  function applyReadOnlyMode(reason = "") {
    if (__cmReadOnly) return;
    __cmReadOnly = true;
    window.__CM_DRAFT_READONLY__ = true;

    const readOnlyMessage =
      reason ||
      "Order placed, please call or text 501-581-0300 if you need to make changes.";

    if (__cmHashPollTimer) {
      clearInterval(__cmHashPollTimer);
      __cmHashPollTimer = null;
    }

    if (__cmAutosaveTimer) {
      clearInterval(__cmAutosaveTimer);
      __cmAutosaveTimer = null;
    }

    if (__cmDebouncedSaveTimer) {
      clearTimeout(__cmDebouncedSaveTimer);
      __cmDebouncedSaveTimer = null;
    }

    __cmDraftDirty = false;

    try {
      const saveBtn = document.getElementById(CM_DRAFTS.MANUAL_BUTTON_ID);
      if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = "Read Only";
        saveBtn.title = "This design is locked after checkout.";
        saveBtn.style.opacity = "0.55";
        saveBtn.style.cursor = "not-allowed";
      }
    } catch (_) {}

    try {
      const finalizeBtn = document.getElementById("pamela-finalize-addtocart");
      if (finalizeBtn) {
        finalizeBtn.disabled = true;
        finalizeBtn.textContent = "Order Complete";
        finalizeBtn.title = "This ordered design is now read only.";
        finalizeBtn.style.opacity = "0.55";
        finalizeBtn.style.cursor = "not-allowed";
      }
    } catch (_) {}

    try {
      ["pamela-side-front", "pamela-side-back"].forEach((id) => {
        const btn = document.getElementById(id);
        if (!btn) return;
        btn.style.position = "relative";
        btn.style.zIndex = "2147483647";
      });
    } catch (_) {}

    try {
      const bannerId = "cm-readonly-banner";
      let banner = document.getElementById(bannerId);
      if (!banner) {
        banner = document.createElement("div");
        banner.id = bannerId;
        banner.style.cssText =
          "position:sticky;top:0;z-index:2147483647;padding:12px 16px;background:#111;color:#fff;" +
          "font:800 13px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;text-align:center;";
        document.body.insertBefore(banner, document.body.firstChild || null);
      }
      banner.textContent = readOnlyMessage;
    } catch (_) {}

    try {
      const topbar = document.getElementById("pamela-editor-topbar");
      const toolbar = document.getElementById("pamela-editor-toolbar");
      const bars = [topbar, toolbar].filter(Boolean);

      bars.forEach((el) => {
        el.style.pointerEvents = "none";
        el.style.userSelect = "none";
        el.style.opacity = "0.28";
      });

      if (bars.length) {
        const parent = bars[0].parentNode || document.body;
        if (parent && parent.style && (!parent.style.position || parent.style.position === "static")) {
          parent.style.position = "relative";
        }

        const overlayId = "cm-readonly-toolbar-cover";
        let cover = document.getElementById(overlayId);

        const minLeft = Math.min(...bars.map((el) => el.offsetLeft || 0));
        const minTop = Math.min(...bars.map((el) => el.offsetTop || 0));
        const maxRight = Math.max(...bars.map((el) => (el.offsetLeft || 0) + (el.offsetWidth || 0)));
        const maxBottom = Math.max(...bars.map((el) => (el.offsetTop || 0) + (el.offsetHeight || 0)));

        if (!cover) {
          cover = document.createElement("div");
          cover.id = overlayId;
          cover.setAttribute("aria-hidden", "true");
          cover.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, true);
          cover.addEventListener("pointerdown", (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, true);
          parent.appendChild(cover);
        }

        cover.style.cssText =
          `position:absolute;left:${minLeft}px;top:${minTop}px;width:${Math.max(0, maxRight - minLeft)}px;` +
          `height:${Math.max(0, maxBottom - minTop)}px;z-index:2147483646;border-radius:18px;` +
          "background:rgba(17,17,17,0.78);display:flex;align-items:center;justify-content:center;" +
          "padding:22px 28px;box-sizing:border-box;text-align:center;color:#fff;" +
          "font:900 20px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:not-allowed;";
        cover.textContent = readOnlyMessage;

        const sideButtons = ["pamela-side-front", "pamela-side-back"]
          .map((id) => document.getElementById(id))
          .filter(Boolean);

        if (sideButtons.length) {
          const switcherId = "cm-readonly-side-switcher";
          let switcher = document.getElementById(switcherId);
          if (!switcher) {
            switcher = document.createElement("div");
            switcher.id = switcherId;
            parent.appendChild(switcher);
          }

          switcher.style.cssText =
            `position:absolute;left:${minLeft}px;top:${maxBottom + 12}px;width:${Math.max(0, maxRight - minLeft)}px;` +
            "display:flex;justify-content:center;gap:12px;z-index:2147483647;pointer-events:auto;";

          sideButtons.forEach((btn) => {
            btn.style.position = "relative";
            btn.style.zIndex = "2147483647";
            btn.style.pointerEvents = "auto";
            btn.style.opacity = "1";
            btn.disabled = false;
            if (btn.parentNode !== switcher) {
              switcher.appendChild(btn);
            }
          });
        }
      }
    } catch (_) {}

    try {
      if (typeof window.PAMELA_CLOSE_ADDONS_UI === "function") {
        window.PAMELA_CLOSE_ADDONS_UI();
      }
      if (typeof window.PAMELA_CLOSE_CLIPART_UI === "function") {
        window.PAMELA_CLOSE_CLIPART_UI();
      }
      if (typeof window.PAMELA_CLOSE_PANELS_UI === "function") {
        window.PAMELA_CLOSE_PANELS_UI();
      }

      const addonFlyout = document.getElementById("pamela-addon-flyout");
      if (addonFlyout) addonFlyout.style.display = "none";
    } catch (_) {}

    try {
      const editor = getEditor();
      const stage = editor?.stage || null;
      const container = stage && typeof stage.container === "function" ? stage.container() : null;
      const host = container?.parentNode || null;

      if (container) {
        container.style.pointerEvents = "none";
        container.style.userSelect = "none";
      }

      if (host && host.style && (!host.style.position || host.style.position === "static")) {
        host.style.position = "relative";
      }

      if (host && container) {
        const shieldId = "cm-readonly-stage-shield";
        let shield = document.getElementById(shieldId);
        if (!shield) {
          shield = document.createElement("div");
          shield.id = shieldId;
          shield.setAttribute("aria-hidden", "true");
          shield.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, true);
          shield.addEventListener("pointerdown", (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, true);
          shield.addEventListener("wheel", (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, { passive: false, capture: true });
          host.appendChild(shield);
        }

        shield.style.cssText =
          `position:absolute;left:${container.offsetLeft || 0}px;top:${container.offsetTop || 0}px;` +
          `width:${container.offsetWidth || 0}px;height:${container.offsetHeight || 0}px;` +
          "z-index:2147483645;background:transparent;cursor:not-allowed;";
      }
    } catch (_) {}

    try {
      const overlayId = "cm-readonly-overlay";
      let overlay = document.getElementById(overlayId);
      if (overlay) {
        overlay.style.display = "none";
      }
    } catch (_) {}
  }

  function setDraftIdentity(token, designId) {
    window.cmDraftToken = token ? String(token) : "";
    window.cmDesignId = designId ? String(designId) : "";
  }

  function clearDraftIdentity() {
    window.cmDraftToken = "";
    window.cmDesignId = "";
    try { localStorage.removeItem("cmDraftToken"); } catch (_) {}
    try { localStorage.removeItem("cmDesignId"); } catch (_) {}
    try { localStorage.removeItem("LAST_RESUME_LINK"); } catch (_) {}
    try { sessionStorage.removeItem("cmDraftToken"); } catch (_) {}
    try { sessionStorage.removeItem("cmDesignId"); } catch (_) {}
    try { sessionStorage.removeItem("LAST_RESUME_LINK"); } catch (_) {}
  }

  function clearLeadIdentity() {
    window.cmLeadId = "";
    try { localStorage.removeItem("cmLeadId"); } catch (_) {}
    try { sessionStorage.removeItem("cmLeadId"); } catch (_) {}
  }

  function resetOrderSessionState() {
    clearDraftIdentity();
  }

  function normalizeLayoutKind(value) {
    const v = String(value || "").trim().toLowerCase();
    if (v === "single" || /\bsingle\b/.test(v) || /\bone[-\s]?person\b/.test(v) || /\bfor[-\s]?one\b/.test(v)) {
      return "single";
    }
    if (
      v === "companion" ||
      /\bcompanion\b/.test(v) ||
      /\bdouble\b/.test(v) ||
      /\btwo[-\s]?person\b/.test(v) ||
      /\bfor[-\s]?two\b/.test(v) ||
      /\b2[-\s]?person\b/.test(v)
    ) {
      return "companion";
    }

    const hasMarkerCue =
      v.includes("grass-level") ||
      v.includes("grass level") ||
      v.includes("flat-marker") ||
      v.includes("flat marker") ||
      /\bmarker\b/.test(v);
    const sizeMatch =
      v.match(/(\d+(?:\.\d+)?)\s*(?:long|l)?\s*[-\s]*x[-\s]*(\d+(?:\.\d+)?)\s*(?:wide|w)?/) ||
      v.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
    const longIn = sizeMatch ? Number(sizeMatch[1]) : 0;
    if (hasMarkerCue && longIn > 0 && longIn <= 36) {
      return "single";
    }

    return "";
  }

  function getLayoutFromUrlLike(rawUrl) {
    try {
      const url = new URL(String(rawUrl || ""), window.location.origin);
      return normalizeLayoutKind(url.searchParams.get("layout"));
    } catch (_) {
      return "";
    }
  }

  function inferDraftLayout(state, draft) {
    const fromCurrentUrl = getLayoutFromUrlLike(window.location.href);
    if (fromCurrentUrl) return fromCurrentUrl;

    const fromCurrentUrlText = normalizeLayoutKind(window.location.href);
    if (fromCurrentUrlText) return fromCurrentUrlText;

    const direct = normalizeLayoutKind(
      state?.layout ||
      state?.layout_type ||
      draft?.layout ||
      draft?.layout_type ||
      draft?.product_layout ||
      draft?.kind ||
      ""
    );
    if (direct) return direct;

    const fromSavedUrl = getLayoutFromUrlLike(
      state?.editor_url ||
      draft?.editor_url ||
      draft?.editorUrl ||
      draft?.resume_url ||
      draft?.resumeUrl ||
      ""
    );
    if (fromSavedUrl) return fromSavedUrl;

    const text = [
      state?.product_handle,
      draft?.product_handle,
      draft?.productHandle,
      state?.variant_title,
      draft?.variant_title,
      draft?.variantTitle
    ].map((part) => String(part || "")).join(" ");

    return normalizeLayoutKind(text);
  }

  async function ensureDraftIdentity() {
    const token = getDraftToken();
    const designId = getDesignId();
    if (token && designId) return { ok: true, token, designId, created: false };

    if (__cmEnsureIdentityPromise) return __cmEnsureIdentityPromise;

    __cmEnsureIdentityPromise = (async () => {
      try {
        return await createDraftIfNeeded();
      } finally {
        __cmEnsureIdentityPromise = null;
      }
    })();

    return __cmEnsureIdentityPromise;
  }

  function currentProductContext() {
    try {
      const url = new URL(window.location.href);
      return {
        product_handle: String(url.searchParams.get("product") || "").trim(),
        variant_id: String(url.searchParams.get("variant") || "").trim(),
        layout: String(url.searchParams.get("layout") || "").trim(),
        style: String(url.searchParams.get("style") || "").trim(),
        editor_url: window.location.href
      };
    } catch (_) {
      return {
        product_handle: "",
        variant_id: "",
        layout: "",
        style: "",
        editor_url: ""
      };
    }
  }

  function safeCall(fnName) {
    try {
      if (typeof window[fnName] === "function") return window[fnName]();
    } catch (_) {}
    return undefined;
  }

  function captureCurrentSideState() {
    try {
      if (typeof window.pamelaSaveAllSideStates === "function") {
        window.pamelaSaveAllSideStates();
      } else if (typeof window.saveCurrentSideState === "function") {
        window.saveCurrentSideState();
      }
    } catch (_) {}

    try {
      if (typeof window.captureBaselineIfMissing === "function") {
        window.captureBaselineIfMissing();
      }
    } catch (_) {}
  }

function buildDraftPayload(editor) {
  captureCurrentSideState();

  const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || null;
  const sides = window.PAMELA_SIDES || {
    current: "front",
    front: { snapshot: null, draft_state: null },
    back: { snapshot: null, draft_state: null },
    baseline: { snapshot: null, draft_state: null }
  };
  const currentSide = (sides.current === "back") ? "back" : "front";

  let thumbnailData = null;
  try {
    const st = editor?.stage || null;
    if (st && typeof st.toDataURL === "function" && typeof st.width === "function") {
      const THUMB_WIDTH = 320;
      const fullWidth = st.width();
      const scale = fullWidth > 0 ? (THUMB_WIDTH / fullWidth) : 1;
      thumbnailData = st.toDataURL({
        mimeType: "image/jpeg",
        quality: 0.72,
        pixelRatio: scale
      });
    }
  } catch (_) {}

  return {
    editor_state: {
      version: 2,
      saved_at: new Date().toISOString(),
      link_expires_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
      delete_after_days: CM_DRAFTS.DELETE_AFTER_DAYS,
      save_mode: "editor-native",
      current_side: currentSide,
      layout: String(editor?.layout || currentProductContext().layout || ""),
      product_handle: String(currentProductContext().product_handle || ""),
      variant_id: String(selectedVariant?.id || currentProductContext().variant_id || ""),
      variant_title: String(selectedVariant?.title || ""),
      editor_url: String(window.location.href || ""),
      front: {
        snapshot: sides.front?.snapshot || null,
        draft_state: sides.front?.draft_state || null
      },
      back: {
        snapshot: sides.back?.snapshot || null,
        draft_state: sides.back?.draft_state || null
      },
      baseline: {
        snapshot: sides.baseline?.snapshot || null,
        draft_state: sides.baseline?.draft_state || null
      }
    },
    thumbnail_data: thumbnailData
  };
}

function countDraftStateContent(ds) {
  if (!ds || typeof ds !== "object") return 0;
  const meaningfulTexts = Array.isArray(ds.texts)
    ? ds.texts.filter((t) => String(t?.text || "").replace(/\s+/g, " ").trim() !== "").length
    : 0;
  return (
    meaningfulTexts +
    (Array.isArray(ds.panels) ? ds.panels.length : 0) +
    (Array.isArray(ds.clipart) ? ds.clipart.length : 0) +
    (Array.isArray(ds.portraits) ? ds.portraits.length : 0) +
    (Array.isArray(ds.laser) ? ds.laser.length : 0)
  );
}

function draftPayloadHasMeaningfulDesign(payload) {
  const state = payload?.editor_state || payload || {};
  return (
    countDraftStateContent(state.front?.draft_state) +
    countDraftStateContent(state.back?.draft_state)
  ) > 0;
}

function getResumeEmailSentKey(token) {
  return `cmResumeEmailSent:${String(token || "").trim()}`;
}

function hasSentResumeEmailForToken(token) {
  const key = getResumeEmailSentKey(token);
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "1" || sessionStorage.getItem(key) === "1";
  } catch (_) {
    return false;
  }
}

function markResumeEmailSentForToken(token) {
  const key = getResumeEmailSentKey(token);
  if (!key) return;
  try { localStorage.setItem(key, "1"); } catch (_) {}
  try { sessionStorage.setItem(key, "1"); } catch (_) {}
}

function buildStableResumeUrl(token) {
  const t = String(token || "").trim();
  if (!t) return "";

  try {
    if (typeof window.buildEditorReturnUrl === "function") {
      return window.buildEditorReturnUrl({ token: t });
    }
  } catch (_) {}

  try {
    const u = new URL(window.location.href);
    u.searchParams.set("token", t);
    u.searchParams.delete("readonly");
    u.searchParams.delete("read_only");
    u.searchParams.delete("view_only");
    const mode = String(u.searchParams.get("mode") || "").trim().toLowerCase();
    if (mode === "readonly" || mode === "read-only" || mode === "view" || mode === "view-only") {
      u.searchParams.delete("mode");
    }
    return u.toString();
  } catch (_) {
    return "";
  }
}

async function sendResumeEmailOnce(token, opts = {}) {
  const t = String(token || "").trim();
  const leadId = getLeadId();
  if (!t || !leadId || __cmReadOnly) return { ok: false, reason: "missing_identity" };
  if (hasSentResumeEmailForToken(t)) return { ok: true, reason: "already_sent" };

  const resumeUrl = buildStableResumeUrl(t);
  if (!resumeUrl) return { ok: false, reason: "missing_url" };

  const payload = {
    lead_id: leadId,
    token: t,
    read_only: false,
    mode: "resume",
    link_expires_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
    archive_after_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
    delete_after_days: CM_DRAFTS.DELETE_AFTER_DAYS,
    expired_message: CM_DRAFTS.EXPIRED_MESSAGE,
    resume_url: resumeUrl
  };

  markResumeEmailSentForToken(t);

  try {
    if (opts.keepalive && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      const queued = navigator.sendBeacon(`${CM_DRAFTS.API_BASE}/api/send-resume-link`, blob);
      return { ok: !!queued, queued: !!queued, resume_url: resumeUrl };
    }

    const resp = await fetch(`${CM_DRAFTS.API_BASE}/api/send-resume-link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: !!opts.keepalive
    });

    if (!resp.ok) {
      try { localStorage.removeItem(getResumeEmailSentKey(t)); } catch (_) {}
      try { sessionStorage.removeItem(getResumeEmailSentKey(t)); } catch (_) {}
      const txt = await resp.text().catch(() => "");
      throw new Error(`send-resume-link failed (${resp.status}): ${txt}`);
    }

    const data = await resp.json().catch(() => ({}));
    return { ok: true, resume_url: data.resume_url || resumeUrl };
  } catch (err) {
    console.warn("Resume email send failed:", err);
    return { ok: false, error: err };
  }
}

function buildHash(editor) {
  const payload = buildDraftPayload(editor);
  const state = payload?.editor_state || {};
  return JSON.stringify({
    current_side: state.current_side || "front",
    front_snapshot: state.front?.snapshot || null,
    back_snapshot: state.back?.snapshot || null,
    front_draft_state: state.front?.draft_state || null,
    back_draft_state: state.back?.draft_state || null
  });
}

async function saveDraftNow(opts = {}) {
  if (__cmReadOnly) return { ok: true, reason: "read_only" };

  const editor = getEditor();
  if (!editor) return { ok: false, reason: "no_editor" };

  let token = getDraftToken();
  let designId = getDesignId();

  if (!token || !designId) {
    const created = await ensureDraftIdentity();
    if (!created?.ok) return { ok: false, reason: "missing_identity", detail: created };
    token = String(created.token || "").trim();
    designId = String(created.designId || "").trim();
  }

  const payload = buildDraftPayload(editor);
  const hasMeaningfulDesign = draftPayloadHasMeaningfulDesign(payload);
  if (!hasMeaningfulDesign && opts.allowBlank !== true) {
    return { ok: false, reason: "blank_design_not_saved" };
  }

  const body = JSON.stringify({
    token,
    design_id: designId,
    link_expires_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
    delete_after_days: CM_DRAFTS.DELETE_AFTER_DAYS,
    archive_after_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
    editor_state: payload.editor_state || {},
    thumbnail_data: payload.thumbnail_data || null
  });

  try {
    const resp = await fetch(`${CM_DRAFTS.API_BASE}/api/save-draft`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: !!opts.keepalive
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`save-draft failed (${resp.status}): ${txt}`);
    }

    __cmDraftDirty = false;
    if (__cmDebouncedSaveTimer) {
      clearTimeout(__cmDebouncedSaveTimer);
      __cmDebouncedSaveTimer = null;
    }
    try { __cmLastHash = buildHash(editor); } catch (_) {}
    if (hasMeaningfulDesign && opts.sendResumeEmail !== false) {
      await sendResumeEmailOnce(token, { keepalive: !!opts.keepalive });
    }
    return { ok: true };
  } catch (err) {
    console.warn("Draft save failed:", err);
    return { ok: false, error: err };
  }
}

async function fetchDraft(token) {
  const t = String(token || "").trim();
  if (!t) return { ok: false, reason: "missing_token" };

  try {
    const resp = await fetch(`${CM_DRAFTS.API_BASE}/api/draft/${encodeURIComponent(t)}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      if (resp.status === 404 || resp.status === 410) {
        applyExpiredDesignMode(CM_DRAFTS.DELETED_MESSAGE);
        return { ok: false, reason: "deleted", error: new Error(txt || "Draft no longer available") };
      }
      throw new Error(`draft fetch failed (${resp.status}): ${txt}`);
    }

    const data = await resp.json();
    const draft = data?.draft || null;
    const designId =
      String(
        draft?.design_id ||
        draft?.designId ||
        data?.design_id ||
        data?.designId ||
        ""
      ).trim();

    if (t && designId) {
      setDraftIdentity(t, designId);
    } else if (t) {
      window.cmDraftToken = t;
    }

    const lifecycleStatus = getDraftLifecycleStatus(draft);
    if (lifecycleStatus === "deleted") {
      void markDraftLifecycle(t, draft, lifecycleStatus);
      applyExpiredDesignMode(CM_DRAFTS.DELETED_MESSAGE);
      return { ok: false, reason: "deleted", draft };
    }

    if (lifecycleStatus === "expired") {
      void markDraftLifecycle(t, draft, lifecycleStatus);
      applyExpiredDesignMode(CM_DRAFTS.EXPIRED_MESSAGE);
      return { ok: false, reason: "expired", draft };
    }

    if (getReadOnlyFromUrl() || isReadOnlyDraft(draft)) {
      applyReadOnlyMode();
    }

    return { ok: true, draft };
  } catch (err) {
    console.warn("Draft fetch failed:", err);
    return { ok: false, error: err };
  }
}

function normalizeTemplateDraft(data) {
  const template =
    data?.template ||
    data?.draft ||
    data?.preloaded_design ||
    data?.design ||
    data ||
    null;

  if (!template || typeof template !== "object") return null;

  const editorState =
    template.editor_state_json ||
    template.editor_state ||
    template.editorState ||
    data?.editor_state_json ||
    data?.editor_state ||
    null;

  if (!editorState || typeof editorState !== "object") return null;

  return {
    ...template,
    editor_state_json: editorState,
    template_id: String(
      template.template_id ||
      template.templateId ||
      template.id ||
      data?.template_id ||
      data?.templateId ||
      ""
    ).trim()
  };
}

function getTemplateIdFromUrl() {
  try {
    const url = new URL(window.location.href);
    return String(
      url.searchParams.get("template_id") ||
      url.searchParams.get("preloaded_design_id") ||
      url.searchParams.get("design_template_id") ||
      ""
    ).trim();
  } catch (_) {
    return "";
  }
}

function templateAdminEnabled() {
  try {
    const url = new URL(window.location.href);
    const direct = String(
      url.searchParams.get("template_admin") ||
      url.searchParams.get("preloaded_admin") ||
      ""
    ).trim().toLowerCase();

    if (["1", "true", "yes", "admin"].includes(direct)) return true;
  } catch (_) {}

  try {
    return String(localStorage.getItem("CM_TEMPLATE_ADMIN") || "").trim() === "1";
  } catch (_) {
    return false;
  }
}

function getTemplateAdminKeyFromUrl() {
  try {
    const url = new URL(window.location.href);
    return String(
      url.searchParams.get("template_key") ||
      url.searchParams.get("admin_key") ||
      ""
    ).trim();
  } catch (_) {
    return "";
  }
}

async function fetchTemplate(templateId) {
  const id = String(templateId || "").trim();
  if (!id) return { ok: false, reason: "missing_template_id" };

  try {
    const resp = await fetch(`${CM_DRAFTS.API_BASE}/api/editor-template/${encodeURIComponent(id)}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`template fetch failed (${resp.status}): ${txt}`);
    }

    const data = await resp.json();
    const template = normalizeTemplateDraft(data);
    if (!template?.editor_state_json) {
      throw new Error("template fetch returned missing editor_state");
    }

    return { ok: true, template };
  } catch (err) {
    console.warn("Template fetch failed:", err);
    return { ok: false, error: err };
  }
}

async function saveTemplateNow(opts = {}) {
  const editor = getEditor();
  if (!editor) return { ok: false, reason: "no_editor" };

  const payload = buildDraftPayload(editor);
  if (!draftPayloadHasMeaningfulDesign(payload) && opts.allowBlank !== true) {
    return { ok: false, reason: "blank_design_not_saved" };
  }
  const ctx = currentProductContext();
  const adminKey = String(opts.adminKey || getTemplateAdminKeyFromUrl() || "").trim();

  const saveUrl = new URL(`${CM_DRAFTS.API_BASE}/api/editor-template`);
  if (adminKey) saveUrl.searchParams.set("template_key", adminKey);

  try {
    const resp = await fetch(saveUrl.toString(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        template_id: String(opts.template_id || "").trim() || undefined,
        product_handle: ctx.product_handle,
        variant_id: ctx.variant_id,
        layout: ctx.layout,
        style: ctx.style,
        editor_url: ctx.editor_url,
        editor_state: payload.editor_state || {},
        thumbnail_data: payload.thumbnail_data || null
      })
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`template save failed (${resp.status}): ${txt}`);
    }

    const data = await resp.json();
    const templateId = String(
      data?.template_id ||
      data?.templateId ||
      data?.id ||
      data?.template?.template_id ||
      data?.template?.id ||
      ""
    ).trim();

    if (!templateId) throw new Error("template save returned missing template_id");

    try { window.cmTemplateId = templateId; } catch (_) {}
    return { ok: true, templateId, response: data };
  } catch (err) {
    console.warn("Template save failed:", err);
    return { ok: false, error: err };
  }
}

async function createDraftIfNeeded() {
  let token = getDraftToken();
  let designId = getDesignId();

  if (token && designId) {
    return { ok: true, token, designId, created: false };
  }

  const leadId = getLeadId();
  const ctx = currentProductContext();

  if (!leadId) {
    return { ok: false, reason: "missing_lead_id" };
  }

  const resp = await fetch(`${CM_DRAFTS.API_BASE}/api/create-editor-draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      lead_id: leadId,
      product_handle: ctx.product_handle,
      variant_id: ctx.variant_id,
      layout: ctx.layout,
      style: ctx.style,
      editor_url: ctx.editor_url,
      link_expires_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
      delete_after_days: CM_DRAFTS.DELETE_AFTER_DAYS,
      archive_after_days: CM_DRAFTS.LINK_EXPIRES_DAYS,
      send_resume_email: false
    })
  });

  if (!resp.ok) {
    const txt = await resp.text().catch(() => "");
    throw new Error(`create-editor-draft failed (${resp.status}): ${txt}`);
  }

  const data = await resp.json();
  token = String(data?.token || "").trim();
  designId = String(data?.design_id || "").trim();

  if (!token || !designId) {
    throw new Error("create-editor-draft returned missing token/design_id");
  }

  setDraftIdentity(token, designId);
  return { ok: true, token, designId, created: true, response: data };
}

async function setVisibleSide(side) {
  try {
    if (typeof window.switchToSide === "function") {
      await window.switchToSide(side);
      return;
    }
  } catch (e) {
    console.warn("switchToSide failed:", e);
  }

  try {
    if (window.__CM_BACK_LETTERING__?.setSide) {
      window.__CM_BACK_LETTERING__.setSide(side);
    }
  } catch (_) {}

  window.PAMELA_SIDES = window.PAMELA_SIDES || {};
  window.PAMELA_SIDES.current = side;
}

async function applyDraftToEditor(editor, draft) {
  try {
    const state = draft?.editor_state_json || null;
    if (!state || !editor) return false;

    const restoredLayout = inferDraftLayout(state, draft);

    if (restoredLayout) {
      editor.layout = restoredLayout;
      editor.productLayout = restoredLayout;
      window.__PAMELA_CURRENT_LAYOUT__ = restoredLayout;
      window.PAMELA_CURRENT_LAYOUT = restoredLayout;
    }

    const savedVariantId = String(
      state?.variant_id ||
      draft?.variant_id ||
      draft?.variantId ||
      ""
    ).trim();

    window.PAMELA_SIDES = window.PAMELA_SIDES || {
      current: "front",
      front: { snapshot: null, draft_state: null },
      back: { snapshot: null, draft_state: null },
      baseline: { snapshot: null, draft_state: null }
    };

    window.PAMELA_SIDES.front = {
      snapshot: state?.front?.snapshot || null,
      draft_state: state?.front?.draft_state || null
    };

    window.PAMELA_SIDES.back = {
      snapshot: state?.back?.snapshot || null,
      draft_state: state?.back?.draft_state || null
    };

    window.PAMELA_SIDES.baseline = {
      snapshot: state?.baseline?.snapshot || null,
      draft_state: state?.baseline?.draft_state || null
    };

    const frontDraftState = window.PAMELA_SIDES.front?.draft_state || null;
    const backDraftState = window.PAMELA_SIDES.back?.draft_state || null;
    const baselineDraftState = window.PAMELA_SIDES.baseline?.draft_state || null;

    console.warn("applyDraftToEditor side counts", {
  frontTexts: Array.isArray(frontDraftState?.texts) ? frontDraftState.texts.length : 0,
  frontClipart: Array.isArray(frontDraftState?.clipart) ? frontDraftState.clipart.length : 0,
  frontLaser: Array.isArray(frontDraftState?.laser) ? frontDraftState.laser.length : 0,
  frontPortraits: Array.isArray(frontDraftState?.portraits) ? frontDraftState.portraits.length : 0,
  backTexts: Array.isArray(backDraftState?.texts) ? backDraftState.texts.length : 0,
  backClipart: Array.isArray(backDraftState?.clipart) ? backDraftState.clipart.length : 0,
  backLaser: Array.isArray(backDraftState?.laser) ? backDraftState.laser.length : 0,
  backPortraits: Array.isArray(backDraftState?.portraits) ? backDraftState.portraits.length : 0,
  baselineTexts: Array.isArray(baselineDraftState?.texts) ? baselineDraftState.texts.length : 0,
  baselineClipart: Array.isArray(baselineDraftState?.clipart) ? baselineDraftState.clipart.length : 0,
  baselineLaser: Array.isArray(baselineDraftState?.laser) ? baselineDraftState.laser.length : 0,
  baselinePortraits: Array.isArray(baselineDraftState?.portraits) ? baselineDraftState.portraits.length : 0
});

    function hasMeaningfulDraftState(ds) {
      if (!ds || typeof ds !== "object") return false;

      return countDraftStateContent(ds) > 0;
    }

    function wireSideButton(btn, side) {
      if (!btn) return;

      const fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);

      fresh.addEventListener("click", async (e) => {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) {}

        try {
          if (typeof window.switchToSide === "function") {
            await window.switchToSide(side);
          } else if (window.__CM_BACK_LETTERING__?.setSide) {
            window.__CM_BACK_LETTERING__.setSide(side);
          }
        } catch (err) {
          console.warn(`Side switch failed for ${side}:`, err);
        }
      });

      return fresh;
    }

    function ensureResumeFrontBackButtons() {
      const fs = document.getElementById("pamela-fullscreen");
      if (!fs || !fs.parentNode) return false;

      let frontBtn = document.getElementById("pamela-side-front");
      let backBtn = document.getElementById("pamela-side-back");

      if (!frontBtn) {
        const sample = document.getElementById("pamela-zoom-out") || fs;
        const baseCss = (sample && sample.style && sample.style.cssText) ? sample.style.cssText : "";

        frontBtn = document.createElement("button");
        frontBtn.type = "button";
        frontBtn.id = "pamela-side-front";
        frontBtn.textContent = "FRONT";
        frontBtn.style.cssText = baseCss;
        frontBtn.style.minWidth = "78px";
        frontBtn.title = "View the front of the monument";
        fs.parentNode.insertBefore(frontBtn, fs);
      }

      if (!backBtn) {
        const sample = document.getElementById("pamela-zoom-out") || fs;
        const baseCss = (sample && sample.style && sample.style.cssText) ? sample.style.cssText : "";

        backBtn = document.createElement("button");
        backBtn.type = "button";
        backBtn.id = "pamela-side-back";
        backBtn.textContent = "BACK";
        backBtn.style.cssText = baseCss;
        backBtn.style.minWidth = "78px";
        backBtn.title = "View the back of the monument";
        fs.parentNode.insertBefore(backBtn, fs);
      }

      wireSideButton(frontBtn, "front");
      wireSideButton(backBtn, "back");

      return true;
    }

    function removeResumeFrontBackButtons() {
      try {
        const frontBtn = document.getElementById("pamela-side-front");
        const backBtn = document.getElementById("pamela-side-back");
        if (frontBtn) frontBtn.remove();
        if (backBtn) backBtn.remove();
      } catch (_) {}
    }

    const hasFrontContent = hasMeaningfulDraftState(frontDraftState);
    const hasBackContent = hasMeaningfulDraftState(backDraftState);

let sideToApply = "front";

// Resume always opens on FRONT if FRONT actually has content.
// Otherwise prefer BACK over baseline.
if (hasFrontContent) {
  sideToApply = "front";
} else if (hasBackContent) {
  sideToApply = "back";
} else if (baselineDraftState) {
  sideToApply = "front";
}

if (!hasFrontContent && !hasBackContent && !baselineDraftState) {
  console.warn("No draft_state found for restore");
  return false;
}

if (typeof window.CM_restoreEditorNativeState !== "function") {
  console.warn("CM_restoreEditorNativeState not available");
  return false;
}

if (savedVariantId) {
  try {
    const variantSelect = document.getElementById("pamela-variant-select");
    if (variantSelect && String(variantSelect.value) !== savedVariantId) {
      variantSelect.value = savedVariantId;
      variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  } catch (variantErr) {
    console.warn("Saved variant restore failed:", variantErr);
  }

}

async function activateSideForDraftRestore(side) {
  const normalized = side === "back" ? "back" : "front";
  try {
    window.__PAMELA_SIDE_SWITCHING__ = true;
    if (window.__CM_BACK_LETTERING__?.setSide) {
      window.__CM_BACK_LETTERING__.setSide(normalized);
    }
    window.PAMELA_SIDES.current = normalized;
    try {
      const applySideAssets = window.__PAMELA_APPLY_SIDE_ASSETS__;
      if (typeof applySideAssets === "function") {
        await applySideAssets(normalized, { resetView: false });
      }
    } catch (assetErr) {
      console.warn(`Draft restore side asset swap failed for ${normalized}:`, assetErr);
    }
  } catch (_) {
    window.PAMELA_SIDES.current = normalized;
  } finally {
    window.__PAMELA_SIDE_SWITCHING__ = false;
  }
}

async function restoreDraftStateToSide(side, draftState) {
  if (!draftState) return false;
  await activateSideForDraftRestore(side);
  window.__PAMELA_RESTORING_SIDE_STATE__ = true;
  try {
    await window.CM_restoreEditorNativeState(editor, draftState);
    return true;
  } finally {
    window.__PAMELA_RESTORING_SIDE_STATE__ = false;
  }
}

let restoredAnySide = false;
window.__PAMELA_DRAFT_RESTORING__ = true;
window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = true;
try {
  if (hasFrontContent) restoredAnySide = (await restoreDraftStateToSide("front", frontDraftState)) || restoredAnySide;
  if (hasBackContent) restoredAnySide = (await restoreDraftStateToSide("back", backDraftState)) || restoredAnySide;
  if (!restoredAnySide && baselineDraftState) {
    restoredAnySide = await restoreDraftStateToSide("front", baselineDraftState);
  }
  if (!restoredAnySide) {
    window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
    return false;
  }

  await activateSideForDraftRestore(sideToApply);

  // The native restore above creates the saved nodes after the editor's
  // initial variant pass. Reapply the active variant now so resumed gray
  // designs behave exactly like fresh designs when changed to black: text
  // turns white and non-explicit panels/clipart use the white-outline style.
  try {
    const applyVariantAppearance = window.PAMELA_applyVariantAppearance;
    const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || null;
    if (typeof applyVariantAppearance === "function" && selectedVariant) {
      await applyVariantAppearance(editor, selectedVariant, { saveSideState: false });
    }
  } catch (appearanceErr) {
    console.warn("Draft restore variant appearance refresh failed:", appearanceErr);
  }
} finally {
  window.__PAMELA_DRAFT_RESTORING__ = false;
}

try { window.PAMELA_refreshLiveTotalAfterRestore?.(); } catch (_) {}

if (hasBackContent) {
  ensureResumeFrontBackButtons();
} else {
  removeResumeFrontBackButtons();
}

try { editor.layers?.background?.batchDraw?.(); } catch (_) {}
try { editor.layers?.panels?.batchDraw?.(); } catch (_) {}
try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
try { editor.layers?.portraits?.batchDraw?.(); } catch (_) {}
try { editor.layers?.border?.batchDraw?.(); } catch (_) {}
try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}
try { editor.stage?.batchDraw?.(); } catch (_) {}
try { window.PAMELA_refreshLiveTotalAfterRestore?.(); } catch (_) {}
try { editor.refreshButtons?.(); } catch (_) {}
try { editor.refreshSizeButtons?.(); } catch (_) {}
try { editor._refreshClipButtons?.(); } catch (_) {}
try { editor._refreshFontUI?.(); } catch (_) {}
try {
  setTimeout(() => {
    window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
  }, 1000);
} catch (_) {
  window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
}

try { __cmLastHash = buildHash(editor); } catch (_) {}
__cmDraftDirty = false;
return true;

  } catch (err) {
    window.__PAMELA_DRAFT_RESTORING__ = false;
    window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
    console.warn("applyDraftToEditor error:", err);
    return false;
  }
}
  function ensureManualSaveButton() {
    if (document.getElementById(CM_DRAFTS.MANUAL_BUTTON_ID)) return;

    const topbarRight = document.getElementById("pamela-topbar-right");
    if (!topbarRight) return;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = CM_DRAFTS.MANUAL_BUTTON_ID;
    btn.textContent = "Save My Design";
    btn.style.cssText =
      "padding:12px 14px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
      "font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";

    btn.addEventListener("click", async () => {
      btn.disabled = true;
      const old = btn.textContent;
      btn.textContent = "Saving…";
      try {
        await saveDraftNow();
        btn.textContent = "Saved";
      } catch (_) {
        btn.textContent = "Save Failed";
      }
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = old;
      }, 1200);
    });

    topbarRight.insertBefore(btn, topbarRight.firstChild);

    if (__cmReadOnly) {
      btn.disabled = true;
      btn.textContent = "Read Only";
      btn.style.opacity = "0.55";
      btn.style.cursor = "not-allowed";
    }
  }

  function ensureTemplateSaveButton() {
    if (!templateAdminEnabled()) return;
    if (document.getElementById(CM_DRAFTS.TEMPLATE_BUTTON_ID)) return;

    const topbarRight = document.getElementById("pamela-topbar-right");
    if (!topbarRight) return;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = CM_DRAFTS.TEMPLATE_BUTTON_ID;
    btn.textContent = "Save as Preloaded Design";
    btn.title = "Save this editor layout as a permanent reusable template.";
    btn.style.cssText =
      "padding:12px 14px;border-radius:10px;border:1px solid #0f766e;background:#0f766e;color:#fff;" +
      "font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";

    btn.addEventListener("click", async () => {
      const adminKey = String(
        getTemplateAdminKeyFromUrl() ||
        window.CM_TEMPLATE_ADMIN_KEY ||
        prompt("Paste your template admin key")
      ).trim();

      if (!adminKey) {
        alert("Missing template admin key.");
        return;
      }

      btn.disabled = true;
      const old = btn.textContent;
      btn.textContent = "Saving template...";

      const result = await saveTemplateNow({ adminKey });
      if (result?.ok && result.templateId) {
        const templateId = result.templateId;
        btn.textContent = `Template ${templateId}`;

        try {
          await navigator.clipboard?.writeText(templateId);
          alert(`Preloaded design saved.\n\nTemplate ID copied:\n${templateId}`);
        } catch (_) {
          alert(`Preloaded design saved.\n\nTemplate ID:\n${templateId}`);
        }
      } else {
        btn.textContent = "Template Save Failed";
        const msg = result?.error?.message || result?.reason || "Unknown error";
        alert(`Template save failed:\n${msg}`);
      }

      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = old;
      }, 2200);
    });

    topbarRight.insertBefore(btn, topbarRight.firstChild);
  }

  function installDirtyWatcher(editor) {
    if (__cmReadOnly) return;
    if (__cmHashPollTimer) clearInterval(__cmHashPollTimer);

    try { __cmLastHash = buildHash(editor); } catch (_) { __cmLastHash = ""; }

    function scheduleDebouncedSave() {
      if (__cmReadOnly) return;
      if (__cmDebouncedSaveTimer) clearTimeout(__cmDebouncedSaveTimer);
      __cmDebouncedSaveTimer = setTimeout(async () => {
        __cmDebouncedSaveTimer = null;
        if (!__cmDraftDirty) return;
        await saveDraftNow();
      }, CM_DRAFTS.CHANGE_SAVE_DELAY_MS);
    }

    __cmHashPollTimer = setInterval(() => {
      try {
        const next = buildHash(editor);
        if (next !== __cmLastHash) {
          __cmDraftDirty = true;
          __cmLastHash = next;
          void ensureDraftIdentity();
          scheduleDebouncedSave();
        }
      } catch (_) {}
    }, CM_DRAFTS.HASH_POLL_MS);
  }

  function installAutosave() {
    if (__cmReadOnly) return;
    if (__cmAutosaveTimer) clearInterval(__cmAutosaveTimer);

    __cmAutosaveTimer = setInterval(async () => {
      if (!__cmDraftDirty) return;
      if (__cmDebouncedSaveTimer) {
        clearTimeout(__cmDebouncedSaveTimer);
        __cmDebouncedSaveTimer = null;
      }
      await saveDraftNow();
    }, CM_DRAFTS.AUTOSAVE_MS);
  }

  function installFinalizeSave() {
    const btn = document.getElementById("pamela-finalize-addtocart");
    if (!btn || btn.__cmDraftSaveWired) return;
    btn.__cmDraftSaveWired = true;

    btn.addEventListener("click", () => {
      if (__cmReadOnly) return;
      saveDraftNow();
    }, true);
  }

  function installBeforeUnloadSave() {
    if (window.__cmDraftBeforeUnloadWired) return;
    window.__cmDraftBeforeUnloadWired = true;

    window.addEventListener("beforeunload", () => {
      if (__cmReadOnly) return;
      if (!__cmDraftDirty) return;
      saveDraftNow({ keepalive: true });
    });
  }

  async function boot() {
  if (__cmDraftBooted) return;
  __cmDraftBooted = true;

const token = getTokenFromUrl();
const treatAsFreshStart = !!token && shouldIgnoreResumeTokenForFreshStart();
  const shouldShowRestoreOverlay = !!token && !treatAsFreshStart;

  if (shouldShowRestoreOverlay) {
    setResumeStartGuidanceSuppressed(true);
  }

  const editor = await waitForEditor();
    ensureManualSaveButton();
    ensureTemplateSaveButton();

    clearDraftIdentity();

  if (shouldShowRestoreOverlay) {
    setResumeRestoreOverlayVisible(true);
  }

if (treatAsFreshStart) {
  stripDraftParamsFromUrl();
}

if (!treatAsFreshStart && getReadOnlyFromUrl()) {
  applyReadOnlyMode();
}
try {
  if (token && !treatAsFreshStart) {
    window.cmDraftToken = token;
    let restored = false;

    for (let i = 0; i < 20; i++) {
      const editorReady =
        editor &&
        editor.stage &&
        editor.layers &&
        editor.layers.content &&
        editor.layers.panels &&
        editor.layers.portraits;

      if (editorReady) {
        const fetched = await fetchDraft(token);
        if (fetched?.ok && fetched.draft?.editor_state_json) {
          restored = await applyDraftToEditor(editor, fetched.draft);
        }
        break;
      }

      await new Promise((r) => setTimeout(r, 150));
    }

    if (!restored) {
      console.warn("Draft restore did not complete.");
    }
  } else {
    const templateId = getTemplateIdFromUrl();
    if (templateId) {
      let restoredTemplate = false;

      for (let i = 0; i < 20; i++) {
        const editorReady =
          editor &&
          editor.stage &&
          editor.layers &&
          editor.layers.content &&
          editor.layers.panels &&
          editor.layers.portraits;

        if (editorReady) {
          const fetched = await fetchTemplate(templateId);
          if (fetched?.ok && fetched.template?.editor_state_json) {
            restoredTemplate = await applyDraftToEditor(editor, fetched.template);
            if (restoredTemplate && !__cmReadOnly) {
              __cmDraftDirty = true;
              try { await ensureDraftIdentity(); } catch (_) {}
              try { await saveDraftNow(); } catch (_) {}
            }
          }
          break;
        }

        await new Promise((r) => setTimeout(r, 150));
      }

      if (!restoredTemplate) {
        console.warn("Preloaded template restore did not complete.");
      }
    }
  }

} finally {
  if (shouldShowRestoreOverlay) {
    setResumeRestoreOverlayVisible(false);
  }
}

installDirtyWatcher(editor);
installAutosave();
installFinalizeSave();
installBeforeUnloadSave();

  }

  // Public helpers are registered outside boot so template admin mode still
  // works if the draft/resume boot path exits early.
  window.CM_DRAFTS_SAVE_NOW = () => saveDraftNow();
  window.CM_DRAFTS_SEND_RESUME_EMAIL_ONCE = (opts = {}) => sendResumeEmailOnce(getDraftToken(), opts);
  window.CM_DRAFTS_HAS_MEANINGFUL_DESIGN = () => {
    const editor = getEditor();
    if (!editor) return false;
    return draftPayloadHasMeaningfulDesign(buildDraftPayload(editor));
  };
  window.CM_DRAFTS_FETCH = () => fetchDraft(getTokenFromUrl() || getDraftToken());
  window.CM_TEMPLATE_SAVE_NOW = (opts = {}) => saveTemplateNow(opts);
  window.CM_TEMPLATE_FETCH = (templateId) => fetchTemplate(templateId || getTemplateIdFromUrl());
  window.CM_RESET_ORDER_SESSION_STATE = resetOrderSessionState;

  function startTemplateAdminButtonWatcher() {
    if (!templateAdminEnabled()) return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      try { ensureTemplateSaveButton(); } catch (_) {}

      if (document.getElementById(CM_DRAFTS.TEMPLATE_BUTTON_ID) || attempts >= 80) {
        clearInterval(timer);
      }
    }, 250);
  }

  function bootWhenReady() {
    if (__cmBootPromise) return __cmBootPromise;
    __cmBootPromise = boot().catch((e) => {
      console.warn("pamela-drafts boot failed:", e);
    });
    return __cmBootPromise;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      startTemplateAdminButtonWatcher();
      bootWhenReady();
    }, { once: true });
  } else {
    startTemplateAdminButtonWatcher();
    bootWhenReady();
  }
})();
